import { useUserContext } from "../context/UserContext";
import { useRetailerConfig } from "./useRetailerConfig";
import { useUpdateUserConfig } from "./useBotFilters";
import { getUserStoresForRetailer } from "../config/retailers";
import { getRetailerStoreLimit, shouldPromptProStoreUpgrade } from "../utils/retailerStoreLimits";
import { effectiveStoreSlotLimit, getRetailerAccessOverride } from "../utils/userUtils";

/**
 * The current retailer's store slots: which stores are saved, how many the
 * plan allows, whether to suggest Pro, and how to save a new list. Same plan
 * rules as the Change Stores page (BotFiltersPage); used by the store editor
 * in the filters sheet. Mirrors pinto/hooks/useStoreSlotPlan.ts.
 */
export function useStoreSlotPlan() {
  const { user, load } = useUserContext();
  const retailerConfig = useRetailerConfig();
  const updateUserConfig = useUpdateUserConfig();

  const retailer = retailerConfig.retailer;
  const userRetailers = user?.retailers || [];
  const selectedStores = getUserStoresForRetailer(user, retailer).filter(Boolean);
  const accessOverride = user ? getRetailerAccessOverride(user, retailer) : undefined;
  const inheritedStoreLimit = getRetailerStoreLimit(userRetailers, retailer);
  const planStoreLimit = accessOverride === false
    ? 0
    : accessOverride === true
      ? Math.max(1, inheritedStoreLimit)
      : inheritedStoreLimit;
  const filledCount = selectedStores.length;
  const storeLimit = user ? effectiveStoreSlotLimit(planStoreLimit, filledCount, user.allowMoreStores) : 0;
  const showProUpgrade = accessOverride === undefined && shouldPromptProStoreUpgrade(userRetailers, retailer);
  const isAtPlanLimit = filledCount >= storeLimit && storeLimit > 0;

  /** Saves only this retailer's store-ID field; categories and search terms are untouched. */
  const saveStores = async (stores: string[]) => {
    if (!user) return;
    await updateUserConfig.mutateAsync(retailerConfig.prepareUserUpdate({ stores }));
    await load();
  };

  return {
    user,
    retailer,
    retailerName: retailerConfig.config.displayName,
    selectedStores,
    storeLimit,
    showProUpgrade,
    isAtPlanLimit,
    saveStores,
  };
}
